<template lang="pug">
div(:class="isMobileScreen ? 'pt-4 px-4 pb-4' : 'pt-6 px-8 pb-8'")
  //- Header section (desktop only)
  .d-flex.align-center.mb-6(v-if="!isMobileScreen")
    .heading-5.font-semibold.neutral700--text Branches & Warehouses
    v-spacer
    yz-btn(outlined label="Inventory settings" prepend-icon="$yzi-settings-02" size="lg" @click="$router.push('/admin/hq/inventory/settings')")

  //- Desktop: Tabs and actions row
  template(v-if="!isMobileScreen")
    .d-flex.align-center
      yz-tabs(v-model="activeTab" type="line")
        yz-tab(label="Branches")
        yz-tab(label="Warehouses")
      v-spacer
      .d-flex
        yz-search-btn.mr-2(v-model="searchText" @search="onSearch")
        yz-btn(label="Create new warehouse" size="lg" @click="onCreateWarehouse")

  //- Mobile: Compact actions and scrollable tabs
  template(v-else)
    .d-flex.align-center.justify-space-between.mb-2
      yz-search-btn(v-model="searchText" @search="onSearch")
      .d-flex.align-center
        yz-btn.mr-2(icon-only outlined icon="$yzi-settings-02" size="lg" @click="$router.push('/admin/hq/inventory/settings')")
        yz-btn(icon-only icon="$yzi-plus" size="lg" @click="onCreateWarehouse")
    .overflow-x-auto
      .d-flex.align-center(style="width: max-content;")
        div(v-for="tab in tabItems" :key="tab.id")
          .px-3.py-2.cursor-pointer(@click="activeTab = tab.id")
            span.yz-medium-body-sm(:class="activeTab === tab.id ? 'neutral700--text' : 'neutral500--text'") {{ tab.label }}
          .bar-active(v-if="activeTab === tab.id")

  yz-divider.ma-0

  //- Filter row
  filter-row(v-if="isInitialized" :filters.sync="filters" @reset="onResetFilters" @update:filters="onFiltersChange")

  //- Branches tab content
  branches-listing(v-if="isInitialized && activeTab === 0" ref="branchesListing" :filters="filters" :search-text="searchText" :is-hq-mode="true" :current-company-slug="company ? company.slug : null")

  //- Warehouses tab content
  warehouses-listing(v-if="isInitialized && activeTab === 1" ref="warehousesListing" :filters="filters" :search-text="searchText" :is-hq-mode="true")
</template>

<script>
import { mapGetters } from 'vuex'
import FilterRow from '@/components/hq/inventory/branches-warehouses/BranchesWarehousesFilterRow.vue'
import BranchesListing from '@/components/hq/inventory/branches-warehouses/BranchesListing.vue'
import WarehousesListing from '@/components/hq/inventory/branches-warehouses/WarehousesListing.vue'

export default {
  components: {
    FilterRow,
    BranchesListing,
    WarehousesListing
  },
  data() {
    return {
      activeTab: 0,
      searchText: '',
      filters: [],
      warehouses: [],
      isInitialized: false,
      // Tab mapping for URL sync
      tabNameToIndex: {
        branches: 0,
        warehouses: 1
      },
      tabIndexToName: ['branches', 'warehouses']
    }
  },
  computed: {
    ...mapGetters({ company: 'getCompany' }),
    isMobileScreen () {
      return this.$vuetify.breakpoint.xs
    },
    tabItems () {
      return [
        { id: 0, label: 'Branches' },
        { id: 1, label: 'Warehouses' }
      ]
    },
    routeQuery() {
      return this.$route.query
    }
  },
  watch: {
    // Watch tab changes to update URL
    activeTab(newTab) {
      this.updateTabQuery(newTab)
    },
    // Watch URL tab changes (browser back/forward)
    '$route.query.tab': {
      handler(newTabName) {
        if (newTabName) {
          const tabIndex = this.tabNameToIndex[newTabName]
          if (tabIndex !== undefined && tabIndex !== this.activeTab) {
            this.activeTab = tabIndex
          }
        }
      }
    },
    // Watch URL search changes
    '$route.query.search': {
      handler(newSearch) {
        const searchValue = newSearch || ''
        if (this.searchText !== searchValue) {
          this.searchText = searchValue
        }
      }
    },
    // Watch URL filters changes
    '$route.query.filters': {
      handler(newFilters) {
        const decoded = this.deserializeFilters(newFilters)
        if (JSON.stringify(this.filters) !== JSON.stringify(decoded)) {
          this.filters = decoded
        }
      }
    }
  },
  created() {
    this.initializeStateFromRoute()
    this.isInitialized = true
  },
  methods: {
    // Initialize state from URL on page load
    initializeStateFromRoute() {
      const { tab, search, filters } = this.$route.query

      // Initialize tab
      if (tab && this.tabNameToIndex[tab] !== undefined) {
        this.activeTab = this.tabNameToIndex[tab]
      } else {
        this.updateTabQuery(0)
      }

      // Initialize search
      if (search) {
        this.searchText = search
      }

      // Initialize filters
      if (filters) {
        this.filters = this.deserializeFilters(filters)
      }
    },
    // Update tab in URL
    updateTabQuery(tabIndex) {
      const tabName = this.tabIndexToName[tabIndex]
      if (tabName && this.$route.query.tab !== tabName) {
        this.$router.replace({
          query: { ...this.$route.query, tab: tabName }
        }).catch(() => {})
      }
    },
    // Update search in URL
    updateSearchQuery(search) {
      const newQuery = { ...this.$route.query }
      if (search) {
        newQuery.search = search
      } else {
        delete newQuery.search
      }
      if (this.$route.query.search !== newQuery.search) {
        this.$router.replace({ query: newQuery }).catch(() => {})
      }
    },
    // Update filters in URL
    updateFiltersQuery(filters) {
      const newQuery = { ...this.$route.query }
      const serialized = this.serializeFilters(filters)
      if (serialized) {
        newQuery.filters = serialized
      } else {
        delete newQuery.filters
      }
      if (this.$route.query.filters !== newQuery.filters) {
        this.$router.replace({ query: newQuery }).catch(() => {})
      }
    },
    // Serialize filters to base64
    serializeFilters(filters) {
      if (!filters || filters.length === 0) {
        return null
      }
      try {
        const json = JSON.stringify(filters)
        return btoa(encodeURIComponent(json))
      } catch (error) {
        console.error('Error serializing filters:', error)
        return null
      }
    },
    // Deserialize filters from base64
    deserializeFilters(base64String) {
      if (!base64String) {
        return []
      }
      try {
        const json = decodeURIComponent(atob(base64String))
        return JSON.parse(json)
      } catch (error) {
        console.error('Error deserializing filters:', error)
        return []
      }
    },
    onSearch(value) {
      this.searchText = value
      this.updateSearchQuery(value)
      if (this.$refs.branchesListing) {
        this.$refs.branchesListing.fetchBranches()
      }
      if (this.$refs.warehousesListing) {
        this.$refs.warehousesListing.fetchWarehouses()
      }
    },
    onResetFilters() {
      this.filters = []
      this.updateFiltersQuery([])
    },
    // Handle filter changes from FilterRow
    onFiltersChange(newFilters) {
      this.filters = newFilters
      this.updateFiltersQuery(newFilters)
    },
    onCreateWarehouse() {
      // If already on warehouses tab, open dialog directly
      if (this.activeTab === 1 && this.$refs.warehousesListing) {
        this.$refs.warehousesListing.openAddWarehouseDialog()
      } else {
        // Switch to warehouses tab first, then open dialog
        this.activeTab = 1
        this.$nextTick(() => {
          if (this.$refs.warehousesListing) {
            this.$refs.warehousesListing.openAddWarehouseDialog()
          }
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.overflow-x-auto {
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar { display: none; }
  scrollbar-width: none;
}
.bar-active {
  height: 2px;
  width: 100%;
  margin: auto;
  margin-top: -2px;
  background-color: #333;
}
</style>
